import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _ from 'underscore';

import {NucleotideType} from 'biojs/Util';
import DataConnector from 'connect/DataConnector';
import {BulkSetModsParams} from 'constants/SequenceOpConstants';
import {containsMixedBases, isFullUTFlexibilityEnabled} from 'modbio/util';
import NucleotidesEditMenu from 'molbio/monomers/NucleotidesEditMenu';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARK} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import ResultsPager from 'ui-library/ResultsPager';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {RowSelectionMode, ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';

import {ModifiedNucleotidesQuery} from './__generated/ModifiedNucleotidesQuery';
import {MonomerQuery} from './__generated/MonomerQuery';
import {
  DEGENERATE_BASE,
  MissingMonomerReason,
  ModifiedNucleotidesPreferences,
  NucleotideComponent,
  NUCLEOTIDES_TABLE_DEFAULT_PAGE_SIZE,
} from './MonomerConstants';
import MonomerDropdown from './MonomerDropdown';
import {getNaturalBaseMonomer, getNaturalPhosphateMonomer, getNaturalSugarMonomer} from './MonomersUtil';
import {
  clearNucleotideRowSelection,
  deselectNucleotideRows,
  finishEditingNucleotideIndex,
  hoverNucleotideIdx,
  selectNucleotideRows,
  updateNucleotidesTablePaginationOffset,
} from './NucleotidesUIReduxActions';

export type MonomerQueryMonomer = MonomerQuery['monomers'][number];
export type ModifiedNucleotide = NonNullable<
  ModifiedNucleotidesQuery['componentSequence']
>['modifiedNucleotides'][number];

export const ROW_HEIGHT = 30;

export const ALLOWED_PAGE_SIZES = [10, NUCLEOTIDES_TABLE_DEFAULT_PAGE_SIZE, 50];

export const NUCLEOTIDES_COLUMNS = [
  {header: '', accessorKey: 'displayPosition', isCollapsible: false, isSortable: false, width: 80},
  {
    header: 'Sugar',
    accessorKey: 'sugar',
    flexGrow: 5,
    isCollapsible: false,
    isSortable: false,
  },
  {
    header: 'Base',
    accessorKey: 'base',
    flexGrow: 5,
    isCollapsible: false,
    isSortable: false,
  },
  {
    header: "3' Phosphate",
    accessorKey: 'phosphate',
    flexGrow: 5,
    isCollapsible: false,
    isSortable: false,
  },
];

export interface NucleotideRowData {
  position: number;
  displayPosition: number;
  sugar: string;
  phosphate: string | MissingMonomerReason;
  base: string | MissingMonomerReason;
}

export interface NucleotideRow extends ScrollableTableRow {
  data: NucleotideRowData;
  eventHandlers: {
    onAnimationEnd: () => void;
  };
}

type NucleotideTableRowData = Omit<NucleotideRow, 'eventHandlers'>;

export function getNucleotideTableRowData(
  base: string,
  position: number,
  modifiedNucleotide: ModifiedNucleotide | null,
  monomersById: MonomersMap,
  nucleotideType: NucleotideType
): NucleotideTableRowData {
  if (modifiedNucleotide) {
    return {
      id: position.toString(),
      data: {
        position,
        displayPosition: position + 1,
        sugar: modifiedNucleotide.sugarMonomerId,
        phosphate: modifiedNucleotide.phosphateMonomerId || MissingMonomerReason.NULL_MONOMER,
        base:
          modifiedNucleotide.baseMonomerId === DEGENERATE_BASE
            ? MissingMonomerReason.DEGENERATE
            : modifiedNucleotide.baseMonomerId,
      },
    };
  } else {
    const monomers = Object.values(monomersById);
    const baseId = getNaturalBaseMonomer(monomers, base)?.id ?? MissingMonomerReason.DEGENERATE;
    return {
      id: position.toString(),
      data: {
        position,
        displayPosition: position + 1,
        sugar: getNaturalSugarMonomer(monomers, nucleotideType).id,
        phosphate: getNaturalPhosphateMonomer(monomers).id,
        base: baseId,
      },
    };
  }
}

interface EditableCellProps {
  rowData: NucleotideRowData;
  openMenu: NucleotideComponent | null;
  nucleotideComponent: NucleotideComponent;
  nucleotideType: NucleotideType;
  sequenceLength: number;
  onSetOpenMenu: (nucleotideComponent: NucleotideComponent | null) => void;
  onUpdateMod: (position: number, monomerId: string | null, nucleotideComponent: NucleotideComponent) => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const EditableCell = ({
  nucleotideComponent,
  nucleotideType,
  openMenu,
  rowData,
  sequenceLength,
  onSetOpenMenu,
  onUpdateMod,
}: EditableCellProps) => (
  <MonomerDropdown
    nucleotideComponent={nucleotideComponent}
    nucleotideType={nucleotideType}
    includeNullMonomer={
      nucleotideComponent === NucleotideComponent.PHOSPHATE && rowData.position === sequenceLength - 1
    }
    isMenuOpen={openMenu === nucleotideComponent}
    selectedMonomerId={rowData[nucleotideComponent]}
    setMenuOpen={(isMenuOpen) => {
      if (isMenuOpen) {
        onSetOpenMenu(nucleotideComponent);
      } else {
        onSetOpenMenu(null);
      }
    }}
    updateSelectedMonomerId={(monomerId) => {
      let serverMonomerId: string | null = monomerId;
      if (monomerId === MissingMonomerReason.NULL_MONOMER) {
        serverMonomerId = null;
      } else if (monomerId === MissingMonomerReason.DEGENERATE) {
        serverMonomerId = DEGENERATE_BASE;
      }
      onUpdateMod(rowData.position, serverMonomerId, nucleotideComponent);
    }}
  />
);

interface CellProps {
  editable: boolean;
  monomersById: MonomersMap;
  nucleotideComponent: NucleotideComponent;
  nucleotideType: NucleotideType;
  onUpdateMod: (position: number, monomerId: string, monomerType: NucleotideComponent) => void;
  openMenu: OpenMenu;
  rowData: NucleotideRowData;
  setOpenMenu: (openMenu: OpenMenu) => void;
  sequenceLength: number;
}

export const Cell = ({
  editable,
  monomersById,
  nucleotideComponent,
  nucleotideType,
  onUpdateMod,
  openMenu,
  rowData,
  setOpenMenu,
  sequenceLength,
}: CellProps): JSX.Element => {
  if (editable) {
    return (
      <EditableCell
        nucleotideComponent={nucleotideComponent}
        nucleotideType={nucleotideType}
        openMenu={openMenu}
        rowData={rowData}
        sequenceLength={sequenceLength}
        onSetOpenMenu={setOpenMenu}
        onUpdateMod={onUpdateMod}
      />
    );
  } else {
    const monId = rowData[nucleotideComponent];
    const isArchived = monomersById[monId] && monomersById[monId].archiveRecord;
    const getName = (monomer: MonomerQueryMonomer): string =>
      isArchived ? `${monomer.name} (${monomer.archiveRecord!.purpose})` : monomer.name;
    return (
      <div className={css(styles.textCell, isArchived && styles.archivedMonomerCell)}>
        {rowData[nucleotideComponent] === MissingMonomerReason.DEGENERATE
          ? 'Unknown'
          : rowData[nucleotideComponent] === MissingMonomerReason.NULL_MONOMER
            ? 'None'
            : getName(monomersById[monId])}
      </div>
    );
  }
};

export interface MonomersMap {
  [accessor: string]: MonomerQueryMonomer;
}

type OpenMenu = NucleotideComponent | null;

interface Props {
  areNucleotideEditsEnabled: boolean;
  bases: string;
  modifiedNucleotides: Array<ModifiedNucleotide>;
  monomers: Array<MonomerQueryMonomer>;
  onBulkUpdateMods: (params: BulkSetModsParams) => void;
  onUpdateMod: (position: number, monomerId: string, monomerType: NucleotideComponent) => void;
  nucleotideType: NucleotideType;
  sequenceId: string;
  isOligo: boolean;
}

interface EnhancedProps {
  editingNucleotideIndices: Array<number>;
  offset: number;
  onUpdateHoverState: (hoveredIdx: number | null) => void;
  onCompleteEditAnimation: (nucleotideIdx: number) => void;
  onSelectPositions: (positions: Array<number>) => void;
  onDeselectPositions: (positions: Array<number>) => void;
  selectedPositions: Set<number>;
  setOffset: (offset: number) => void;
}

class NucleotidesTableConnector extends DataConnector<Props, EnhancedProps> {
  constructor(props: Props) {
    super(props);
    this.clearSelection = this.clearSelection.bind(this);
  }

  clearSelection(): void {
    this.dispatch(clearNucleotideRowSelection(this.props.sequenceId));
  }

  enhanceProps(): EnhancedProps {
    const state = this.reduxState().ui.molbio.nucleotidesUI[this.props.sequenceId];
    return {
      onUpdateHoverState: (hoveredIdx: number | null) => {
        this.dispatch(hoverNucleotideIdx(this.props.sequenceId, hoveredIdx));
      },
      editingNucleotideIndices: state?.editingNucleotideIndices ?? [],
      onCompleteEditAnimation: (nucleotideIdx: number) => {
        this.dispatch(finishEditingNucleotideIndex(this.props.sequenceId, nucleotideIdx));
      },
      onSelectPositions: (positions: Array<number>) => {
        this.dispatch(selectNucleotideRows(this.props.sequenceId, positions));
      },
      onDeselectPositions: (positions: Array<number>) => {
        this.dispatch(deselectNucleotideRows(this.props.sequenceId, positions));
      },
      selectedPositions: state?.selectedPositions ?? new Set(),
      offset: state?.tablePaginationOffset ?? 0,
      setOffset: (offset: number) => {
        this.dispatch(updateNucleotidesTablePaginationOffset(this.props.sequenceId, offset));
      },
    };
  }
}

export function getRowsData(
  bases: string,
  modifiedNucleotides: Array<ModifiedNucleotide>,
  nucleotideType: NucleotideType,
  rowOffset: number,
  pageSize: number,
  monomersById: MonomersMap
): Array<NucleotideTableRowData> {
  return bases
    .split('')
    .slice(rowOffset, rowOffset + pageSize)
    .map((base, i) => {
      const position = i + rowOffset;
      const modifiedNucleotide = modifiedNucleotides.find((mn) => mn.position === position) || null;
      return getNucleotideTableRowData(base, position, modifiedNucleotide, monomersById, nucleotideType);
    });
}

function NucleotidesTable({
  areNucleotideEditsEnabled,
  bases,
  editingNucleotideIndices,
  modifiedNucleotides,
  monomers,
  nucleotideType,
  offset,
  onBulkUpdateMods,
  onCompleteEditAnimation,
  onDeselectPositions,
  onSelectPositions,
  onUpdateHoverState,
  onUpdateMod,
  selectedPositions,
  setOffset,
  sequenceId,
  isOligo,
}: Props & EnhancedProps): JSX.Element {
  const [editableRowId, setEditableRowId] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [pageSize, setPageSize] = usePreference<number>(
    ModifiedNucleotidesPreferences.NUCLEOTIDES_TABLE_PAGE_SIZE
  );
  const [hasDismissedBanner, setHasDismissedBanner] = usePreference<boolean>(
    ModifiedNucleotidesPreferences.HAS_DISMISSED_UT_FLEXIBILITY_BANNER
  );
  const showMixedBaseBanner =
    isOligo &&
    isFullUTFlexibilityEnabled() &&
    containsMixedBases(modifiedNucleotides, nucleotideType) &&
    !hasDismissedBanner;

  const canEditRow = areNucleotideEditsEnabled;

  function entityDescriptor(): string {
    return isOligo ? 'oligo' : 'sequence';
  }

  const monomersById: MonomersMap = {};
  for (const monomer of monomers) {
    monomersById[monomer.id] = monomer;
  }
  const sequenceLength = bases.length;
  const rowsData = bases
    .split('')
    .slice(offset, offset + pageSize)
    .map((base, i) => {
      const position = i + offset;
      const modifiedNucleotide = modifiedNucleotides.find((mn) => mn.position === position) || null;
      return getNucleotideTableRowData(base, position, modifiedNucleotide, monomersById, nucleotideType);
    });

  const rows = rowsData.map((row, i) => {
    const rowClassName = editingNucleotideIndices.includes(row.data.position)
      ? css(styles.editingRow)
      : undefined;
    return {
      id: row.id,
      isSelected: selectedPositions.has(row.data.position),
      // styling for the row
      className: rowClassName,
      // styling for each individual cell in the row
      classNames: {
        displayPosition: rowClassName,
        sugar: rowClassName,
        phosphate: rowClassName,
        base: rowClassName,
      },
      eventHandlers: {
        onAnimationEnd: () => {
          if (editingNucleotideIndices.length > 0) {
            onCompleteEditAnimation(i);
          }
        },
      },
      data: {
        position: row.data.position,
        displayPosition: row.data.displayPosition,
        sugar: (
          <Cell
            editable={areNucleotideEditsEnabled && editableRowId === row.id}
            monomersById={monomersById}
            nucleotideComponent={NucleotideComponent.SUGAR}
            nucleotideType={nucleotideType}
            onUpdateMod={onUpdateMod}
            openMenu={openMenu}
            rowData={row.data}
            sequenceLength={sequenceLength}
            setOpenMenu={setOpenMenu}
          />
        ),
        phosphate: (
          <Cell
            editable={areNucleotideEditsEnabled && editableRowId === row.id}
            monomersById={monomersById}
            nucleotideComponent={NucleotideComponent.PHOSPHATE}
            nucleotideType={nucleotideType}
            onUpdateMod={onUpdateMod}
            openMenu={openMenu}
            rowData={row.data}
            sequenceLength={sequenceLength}
            setOpenMenu={setOpenMenu}
          />
        ),
        base: (
          <Cell
            editable={areNucleotideEditsEnabled && editableRowId === row.id}
            monomersById={monomersById}
            nucleotideComponent={NucleotideComponent.BASE}
            nucleotideType={nucleotideType}
            onUpdateMod={onUpdateMod}
            openMenu={openMenu}
            rowData={row.data}
            sequenceLength={sequenceLength}
            setOpenMenu={setOpenMenu}
          />
        ),
      },
    };
  });

  const selectedSugarIds = _.unique(
    rowsData
      .filter((r) => selectedPositions.has(r.data.position) && !monomersById[r.data.sugar]?.archiveRecord)
      .map((r) => r.data.sugar)
  );
  const selectedBaseIds = _.unique(
    rowsData
      .filter((r) => selectedPositions.has(r.data.position) && !monomersById[r.data.base]?.archiveRecord)
      .map((r) => r.data.base)
  );
  const selectedPhosphateIds = _.unique(
    rowsData
      .filter((r) => selectedPositions.has(r.data.position) && !monomersById[r.data.phosphate]?.archiveRecord)
      .map((r) => r.data.phosphate)
  );

  return (
    <>
      {showMixedBaseBanner && (
        <div className="u-small-spacing-bottom u-small-spacing-top">
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text={`This ${nucleotideType} oligo contains both Us and Ts.`}
            supportingInfo="Both Uracil and Thymine can now be inserted and displayed as natural bases in all oligos."
            onDismiss={() => {
              setHasDismissedBanner(true);
            }}
          />
        </div>
      )}
      <div className={css(styles.pageOptions)}>
        <div className={css(styles.pageOptions)}>
          <ResultsPager
            allowFastForwardAndBackward={true}
            isLoading={false}
            setOffset={setOffset}
            pagination={{limit: pageSize, offset, total: bases.length}}
          />
          <div className="u-input-line-height">
            <ListingSettingsDropdown
              pageSizeConfig={{
                availablePageSizes: ALLOWED_PAGE_SIZES,
                pageSize,
                onSetPageSize: (pageSize: number) => {
                  setOffset(0);
                  setPageSize(pageSize);
                },
              }}
            />
          </div>
        </div>
        {areNucleotideEditsEnabled && (
          <NucleotidesEditMenu
            {...{
              monomersById,
              nucleotideType,
              onBulkUpdateMods,
              selectedPositions,
              selectedBaseIds,
              selectedPhosphateIds,
              selectedSugarIds,
              sequenceId,
              isOligo,
            }}
          />
        )}
      </div>
      <ScrollableTable
        rowSelectionMode={canEditRow ? RowSelectionMode.MULTIPLE : null}
        columns={NUCLEOTIDES_COLUMNS}
        emptyTableText={`This ${entityDescriptor()} does not contain any nucleotides.`}
        getRowContentHeight={(rowIdx: number) => ROW_HEIGHT}
        headerClassName={css(styles.textCell)}
        isCompact={true}
        onRowMouseEnter={(row: NucleotideRow) => {
          if (canEditRow) {
            setEditableRowId(row.id);
          }
          onUpdateHoverState(row.data.position);
        }}
        onRowMouseLeave={(row: NucleotideRow) => {
          if (openMenu == null) {
            setEditableRowId(null);
          }
          onUpdateHoverState(null);
        }}
        onRowsSelect={(toggledRows: Array<NucleotideRow>, isSelected: boolean) => {
          const toggledPositions = toggledRows.map((r) => r.data.position);
          if (isSelected) {
            onSelectPositions(toggledPositions);
          } else {
            onDeselectPositions(toggledPositions);
          }
        }}
        rows={rows}
      />
    </>
  );
}

const backgroundColorKeyframes = {
  '0%': {
    backgroundColor: 'rgba(235, 135, 21, 0.1)',
  },
  '67%': {
    backgroundColor: 'rgba(235, 135, 21, 0.1)',
  },
  '100%': {
    backgroundColor: 'rgba(235, 135, 21, 0)',
  },
};

const styles = StyleSheet.create({
  editingRow: {
    animationFillMode: 'forwards',
    animationName: [backgroundColorKeyframes],
    animationDuration: '3s',
  },
  pageOptions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  textCell: {
    paddingLeft: '12px',
  },
  archivedMonomerCell: {
    color: GRAY_DARK,
  },
});

export default NucleotidesTableConnector.connect(NucleotidesTable);
